// Клиент `/api/v1/trust_rules*`.
//
// Контракт backend'а: request принимает `paths` / `auto_approve`,
// response отдаёт их как `paths_json` / `auto_approve_json`.
// JWT прикручивает `authedFetch`. Типы вшиты руками параллельно
// с backend Pydantic-схемами — тот же паттерн, что в
// `features/connections/api.ts`.

import { authedFetch } from "../../auth/authedFetch";

// HTTP-коды, которые форма интерпретирует как бизнес-ошибки.
export const HTTP_CONFLICT = 409;
export const HTTP_UNPROCESSABLE_ENTITY = 422;

// Канон auto-approve действий (docs/spec/09-trust-control.md). Backend
// валидирует тот же набор — держим типом, чтобы checkbox'ы не разъехались.
export type AutoApproveAction = "pr_merge" | "dependency_update";

export type TrustRuleDecision = "applied" | "rejected" | "not_matched";

export interface TrustRuleResponse {
  readonly id: string;
  readonly org_id: string;
  readonly name: string;
  readonly paths_json: readonly string[];
  readonly auto_approve_json: readonly AutoApproveAction[];
  readonly is_active: boolean;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface TrustRuleApplicationResponse {
  readonly id: string;
  readonly org_id: string;
  readonly trust_rule_id: string;
  readonly task_id: string;
  readonly decision: TrustRuleDecision;
  readonly applied_at: string;
}

export interface TrustRuleCreateRequest {
  readonly name: string;
  readonly paths: readonly string[];
  readonly auto_approve: readonly AutoApproveAction[];
}

export interface TrustRuleUpdateRequest {
  readonly name?: string;
  readonly paths?: readonly string[];
  readonly auto_approve?: readonly AutoApproveAction[];
  readonly is_active?: boolean;
}

/**
 * HTTP-ошибка с числовым `status`. Форма различает 409 (имя занято) от
 * 422 (валидация) без парсинга body. Зеркало `connections.HttpError`.
 */
export class HttpError extends Error {
  public readonly status: number;
  public constructor(status: number, message: string) {
    super(message);
    this.name = "HttpError";
    this.status = status;
  }
}

function buildHeaders(): Record<string, string> {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

export async function fetchTrustRules(): Promise<TrustRuleResponse[]> {
  const response = await authedFetch("/api/v1/trust_rules", {
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `fetchTrustRules: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as TrustRuleResponse[];
}

export async function createTrustRule(
  body: TrustRuleCreateRequest,
): Promise<TrustRuleResponse> {
  const response = await authedFetch("/api/v1/trust_rules", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `createTrustRule: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as TrustRuleResponse;
}

export async function updateTrustRule(
  ruleId: string,
  body: TrustRuleUpdateRequest,
): Promise<TrustRuleResponse> {
  const response = await authedFetch(`/api/v1/trust_rules/${ruleId}`, {
    method: "PATCH",
    headers: buildHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `updateTrustRule: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as TrustRuleResponse;
}

export async function deleteTrustRule(ruleId: string): Promise<void> {
  const response = await authedFetch(`/api/v1/trust_rules/${ruleId}`, {
    method: "DELETE",
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `deleteTrustRule: HTTP ${String(response.status)}`,
    );
  }
}

export async function fetchTrustRuleApplications(): Promise<
  TrustRuleApplicationResponse[]
> {
  const response = await authedFetch("/api/v1/trust_rule_applications", {
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `fetchTrustRuleApplications: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as TrustRuleApplicationResponse[];
}
